import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import { TranslateContext } from "../../context/TranslateContext";
import { useAdminStudio } from "./AdminStudioContext";

export default function AdminStudioBar() {
  const { lang } = React.useContext(TranslateContext);
  const {
    studioUser,
    hasStudioSession,
    users,
    loadingUsers,
    acting,
    applyStudioUser,
    clearStudioSession,
  } = useAdminStudio();

  const [picked, setPicked] = React.useState(null);

  React.useEffect(() => {
    if (studioUser && users.length) {
      const match = users.find((u) => u.uid === studioUser.uid);
      if (match) setPicked(match);
    }
  }, [studioUser, users]);

  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {lang?.adminStudioTitle || "Creator studio (user view)"}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {lang?.adminStudioHint ||
              "Pick a user account to run the same tools they see in the app. Your admin session stays active."}
          </Typography>
        </Box>

        <Autocomplete
          size="small"
          sx={{ minWidth: { xs: "100%", md: 280 } }}
          loading={loadingUsers}
          options={users}
          value={picked}
          onChange={(_, v) => setPicked(v)}
          getOptionLabel={(o) =>
            o?.email ? `${o.name || "User"} · ${o.email}` : o?.name || ""
          }
          isOptionEqualToValue={(a, b) => a?.uid === b?.uid}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={lang?.adminStudioPickUser || "Select user…"}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <PersonOutlined
                      sx={{ ml: 0.5, mr: 0.5, fontSize: 18, opacity: 0.6 }}
                    />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            disabled={!picked?.uid || acting}
            onClick={() => applyStudioUser(picked.uid)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {acting
              ? lang?.loading || "Loading…"
              : lang?.adminStudioApply || "Use this account"}
          </Button>
          {hasStudioSession && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewOutlined />}
                onClick={() => window.open("/user", "_blank")}
                sx={{ textTransform: "none" }}
              >
                {lang?.adminStudioOpenUser || "Open /user"}
              </Button>
              <Button
                variant="text"
                size="small"
                color="inherit"
                startIcon={<LogoutOutlined />}
                onClick={clearStudioSession}
                sx={{ textTransform: "none" }}
              >
                {lang?.adminStudioClear || "End session"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {hasStudioSession && studioUser && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mt: 1.5, fontWeight: 600 }}
          label={
            (lang?.adminStudioActiveAs || "Active as") +
            `: ${studioUser.name || studioUser.email || studioUser.uid}`
          }
        />
      )}

      {!hasStudioSession && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {lang?.adminStudioNeedUser ||
            "Select a user and click “Use this account” before creating ads, characters, or music."}
        </Alert>
      )}
    </Box>
  );
}
